import { Switch, Route } from 'react-router-dom';
import ServerSignup from './server/signup';
import ServerAgent from './server/agent';
import ServerControlPanel from './server/control_panel';
import ServerGfc from './server/gfc';
import ServerMyInfo from './server/myinfo';
import ServerUser from './server/users';
import ServerKeiji from './server/keijis';
import ServerMyPlan from './server/myplan';
import ServerProject from './server/projects';
import ServerPasswordReset from './server/password_reset';
import ServerMyPage from './server/mypage';
import { lazy, LazyExoticComponent, Suspense, useEffect } from 'react';
import { matchPath, RouteComponentProps, useLocation } from 'react-router';
import ServerPlan from './server/plan';
import { go_api_v1 } from './plugins/axios';
import { User } from './types';
import { useDispatch } from 'react-redux';
import { set_user } from './redux/auth';

const NotFound = lazy(() => import('@/pages/404'));
const BankEdit = lazy(() => import('@/pages/bank/edit'));
const Login = lazy(() => import('@/pages/login'));
const Block = lazy(() => import('@/pages/blocks'));
const Favorite = lazy(() => import('@/pages/favorites'));
const ApprovalAge = lazy(() => import('@/pages/approvals/age'));
const PrivateMode = lazy(() => import('@/pages/private_mode'));
const Retire = lazy(() => import('@/pages/retire'));
const About = lazy(() => import('@/pages/about'));
const Schedule = lazy(() => import('@/pages/schedule'));
const Premium = lazy(() => import('@/pages/premium'));
const Setting = lazy(() => import('@/pages/settings/index'));
const JobAdNew = lazy(() => import('@/pages/job_ads/new'));
const JobAdUser = lazy(() => import('@/pages/job_ads/user'));
const JobAdShow = lazy(() => import('@/pages/job_ads/[id]/index'));
const JobAdEdit = lazy(() => import('@/pages/job_ads/[id]/edit'));
const JobAdProject = lazy(() => import('@/pages/job_ads/[id]/projects'));
const KeijiIndex = lazy(() => import('@/pages/keijis'));

interface RouteType {
  path: string;
  title?: string;
  middleware?: (() => void)[];
  component?:
    | LazyExoticComponent<() => JSX.Element>
    | LazyExoticComponent<React.FC<{}>>;
  render?: (props: RouteComponentProps) => React.ReactNode;
  requireAuth?: boolean;
}

const Routes: React.FC = () => {
  const { pathname } = useLocation();
  const routes: RouteType[] = [
    {
      path: '/home',
      title: 'ホーム',
      requireAuth: true,
      component: KeijiIndex,
    },
    {
      path: '/premium',
      title: 'プレミアムプラン',
      requireAuth: true,
      component: Premium,
    },
    {
      path: '/schedule',
      title: '予定の管理',
      requireAuth: true,
      component: Schedule,
    },
    {
      path: '/favorites',
      title: 'お気に入り',
      requireAuth: true,
      component: Favorite,
    },
    {
      path: '/blocks',
      title: 'ブロックリスト',
      requireAuth: true,
      component: Block,
    },
    {
      path: '/settings',
      title: '設定',
      requireAuth: true,
      component: Setting,
    },
    {
      path: '/bank/edit',
      title: '口座情報',
      requireAuth: true,
      component: BankEdit,
    },
    {
      path: '/approvals/age',
      title: '年齢承認',
      requireAuth: true,
      component: ApprovalAge,
    },
    {
      path: '/login',
      title: 'ログイン',
      component: Login,
    },
    {
      path: '/about',
      title: 'GirlsFundingについて',
      requireAuth: true,
      component: About,
    },
    {
      path: '/retire',
      title: '退会',
      requireAuth: true,
      component: Retire,
    },
    {
      path: '/private_mode',
      title: 'シークレットモード',
      requireAuth: true,
      component: PrivateMode,
    },
    {
      path: '/job_ads/new',
      title: '募集の追加',
      requireAuth: true,
      component: JobAdNew,
    },
    {
      path: '/job_ads/user',
      title: '募集一覧',
      requireAuth: true,
      component: JobAdUser,
    },
    {
      path: '/job_ads/:id([0-9]+)',
      requireAuth: true,
      component: JobAdShow,
    },
    {
      path: '/job_ads/:id([0-9]+)/edit',
      requireAuth: true,
      component: JobAdEdit,
    },
    {
      path: '/job_ads/:id([0-9]+)/projects',
      requireAuth: true,
      component: JobAdProject,
    },
    ...ServerMyPage,
    ...ServerKeiji,
    ...ServerUser,
    ...ServerProject,
    ...ServerGfc,
    ...ServerMyInfo,
    ...ServerMyPlan,
    ...ServerSignup,
    ...ServerAgent,
    ...ServerPasswordReset,
    ...ServerControlPanel,
    ...ServerPlan,
    { path: '*', component: NotFound },
  ];

  const route = routes.find((item) => !!matchPath(pathname, item.path));
  const dispatch = useDispatch();
  useEffect(() => {
    if (route === undefined) return;
    if (route.title === undefined) {
      document.title = 'GirlsFunding';
    } else {
      document.title = `${route.title} - GirlsFunding`;
    }
  }, [route, dispatch]);

  const path = route ? route.path : '';
  useEffect(() => {
    const include = ['/home', '/mypage'].includes(path);
    if (!include) return;
    const fetch = async () => {
      const res1 = await go_api_v1.get<User>('/user');
      dispatch(set_user(res1.data));
    };
    fetch();
  }, [path, dispatch]);

  return (
    <Suspense fallback={<></>}>
      <Switch>
        {routes.map((item, key) => {
          return (
            <Route
              exact
              path={item.path}
              component={item.component}
              key={key}
              // @ts-ignore
              render={item.render}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
};

export default Routes;

export type { RouteType };
